import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAtomValue, useSetAtom } from "jotai";

import useWorkspacePermissions from "../../../hooks/useWorkspacePermissions";
import useCloudPermissions from "../../../hooks/useCloudPermissions";
import usePagePermissions from "../../../hooks/usePagePermissions";
import { getErrorMessage } from "../../../utils/GeneralUtils";
import { updateTenantUsers } from "../../../utils/UserUtils";
import { pageSettingsAtom } from "../../../atoms/PageAtoms";
import { tenantIDAtom } from "../../../atoms/GlobalAtoms";
import PageAPI from "../../api/PageAPI";

export type BulkUsersPayload =
  | {
      UserIDs: string[];
      Type: "Update";
      PermissionID: string;
    }
  | {
      Type: "Delete";
      UserIDs: string[];
    };

const useBulkPageUsers = (pageID: string) => {
  const { pPermissions } = usePagePermissions(pageID);
  const { wPermissions } = useWorkspacePermissions();
  const { cPermissions } = useCloudPermissions();
  const queryClient = useQueryClient();
  const setSelectedPage = useSetAtom(pageSettingsAtom);
  const tenantID = useAtomValue(tenantIDAtom);

  const bulkPageUsers = useMutation({
    mutationFn: ({
      workspaceID,
      pageID,
      payload,
    }: {
      workspaceID: string;
      pageID: string;
      payload: BulkUsersPayload;
      onSuccess?: (message: string) => void;
      onError?: () => void;
    }) => {
      if (
        !pPermissions.page_modify_user_permissions &&
        !wPermissions.workspace_modify_user_permissions_page &&
        !cPermissions.cloud_modify_page
      ) {
        return Promise.reject(
          "User does not have permission to update users in this page"
        );
      }

      return PageAPI.bulkdPageUsers(workspaceID, pageID, payload).then(
        (data) => {
          const { users, page } = data.data.data;

          updateTenantUsers(queryClient, tenantID || "", users);
          queryClient.setQueryData(["page", pageID], page);

          setSelectedPage(page);
        }
      );
    },
    onSuccess: (_, { payload, onSuccess }) => {
      const message = `Successfully ${payload.Type}d users.`;
      onSuccess && onSuccess(message);
    },
  });

  return {
    bulkPageUsers: bulkPageUsers.mutate,
    updating: bulkPageUsers.isPending,
    updateBulkError: bulkPageUsers.error
      ? getErrorMessage(bulkPageUsers.error)
      : null,
  };
};

export default useBulkPageUsers;
